import { useSyncExternalStore } from "react";

/**
 * The app's one cabin-audio channel.
 *
 * A clip is played by an <audio> element, and an element that belongs to a
 * page dies with it: the Audio page's soundboard went silent the moment the
 * pilot clicked to any other page (reported 2026-10-04, after the same fault
 * was fixed for the automatic phase clips by moving them into AppLayout).
 *
 * This element belongs to no component. It is created once, on first use, and
 * lives as long as the window, so nothing a page does — mounting, unmounting,
 * re-rendering — can cut a clip off. The automatic phase player (SoundPlayer)
 * and the manual soundboard (Audio page) both play through it, so there is
 * also only ever one announcement at a time, as in a real cabin: starting one
 * stops the other.
 *
 * `nowPlaying` is a key the caller chose ("BOARDING", ...) or null, so the
 * soundboard can light the right button even after the pilot comes back to it.
 */

let el = null;
let nowPlaying = null;
let level = { volume: 1, muted: false };
const listeners = new Set();

function emit(next) {
  if (next === nowPlaying) return;
  nowPlaying = next;
  listeners.forEach((fn) => fn());
}

function element() {
  if (el) return el;
  el = document.createElement("audio");
  el.preload = "none";
  el.addEventListener("ended", () => emit(null));
  el.addEventListener("error", () => emit(null));
  return el;
}

/**
 * Play `url`, replacing whatever is playing. Resolves when playback starts;
 * rejects when it cannot (a missing file, an unsupported codec, autoplay
 * blocked), with nowPlaying already back to null.
 */
export function playClip(url, key) {
  const a = element();
  try {
    a.pause();
    a.src = url;
    a.currentTime = 0;
    a.volume = level.volume;
    a.muted = level.muted;
    emit(key || url);
    const p = a.play();
    if (p && typeof p.then === "function") {
      return p.catch((e) => { emit(null); throw e; });
    }
    return Promise.resolve();
  } catch (e) {
    emit(null);
    return Promise.reject(e);
  }
}

export function stopClip() {
  if (el) {
    el.pause();
    try { el.currentTime = 0; } catch { /* not seekable yet */ }
  }
  emit(null);
}

/** Applied to the clip that is playing now, and to every later one. */
export function setCabinLevel({ volume, muted }) {
  level = { volume: Number.isFinite(volume) ? volume : level.volume, muted: !!muted };
  if (el) {
    el.volume = level.volume;
    el.muted = level.muted;
  }
}

export function getNowPlaying() {
  return nowPlaying;
}

function subscribe(fn) {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

/** The key of the clip playing now, or null; re-renders when it changes. */
export function useNowPlaying() {
  return useSyncExternalStore(subscribe, getNowPlaying, getNowPlaying);
}
